/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Dashboard Header Component with Glassmorphism & Breadcrumbs (Task 10.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';
import { WorkspaceSwitcher } from '../workspace/WorkspaceSwitcher';
import { UserProfileDropdown } from './UserProfileDropdown';
import { WalletBadge } from '../wallet/WalletBadge';
import { LanguageSwitcher } from '../i18n/LanguageSwitcher';
import { ThemeSwitcher } from '../theme/ThemeSwitcher';
import { useUiStore } from '../../stores/useUiStore';
import { useI18nStore } from '../../i18n';
import { NAVIGATION_SECTIONS } from '../../navigation/navigation-items';
import { Menu, ChevronRight, Search } from 'lucide-react';

export interface DashboardHeaderProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({ currentPath, onNavigate }) => {
  const { setMobileDrawerOpen, openCommandPalette } = useUiStore();
  const { t } = useI18nStore();

  // Find active navigation item and section for dynamic breadcrumbs
  const activeNav = useMemo(() => {
    for (const section of NAVIGATION_SECTIONS) {
      const foundItem = section.items.find((item) => item.path === currentPath);
      if (foundItem) {
        return { section, item: foundItem };
      }
    }
    return null;
  }, [currentPath]);

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-surface-border bg-surface/85 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between gap-4 select-none">
      {/* 1. Header Left: Mobile Drawer Trigger & Dynamic Breadcrumbs */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => setMobileDrawerOpen(true)}
          className="md:hidden p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
          aria-label={t('header.openMenu')}
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Dynamic Breadcrumbs Navigation */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-foreground-muted truncate">
          <button
            type="button"
            onClick={() => onNavigate('/workspace/dashboard')}
            className="font-medium text-foreground hover:text-primary transition-colors truncate hidden sm:inline"
          >
            {t('nav.workspace')}
          </button>

          {activeNav && activeNav.item.id !== 'dashboard' ? (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-surface-border hidden sm:inline flex-shrink-0" />
              <span className="truncate hidden sm:inline text-foreground-muted/80">{activeNav.section.title}</span>
              <ChevronRight className="w-3.5 h-3.5 text-surface-border flex-shrink-0" />
              <span className="font-semibold text-foreground truncate">{activeNav.item.label}</span>
            </>
          ) : activeNav && activeNav.item.id === 'dashboard' ? (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-surface-border flex-shrink-0" />
              <span className="font-semibold text-foreground truncate">{activeNav.item.label}</span>
            </>
          ) : null}
        </nav>
      </div>

      {/* 2. Header Right: Quick Search, Language Switcher, Workspace Switcher & User Profile */}
      <div className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0">
        {/* Mobile Search Icon Button */}
        <button
          type="button"
          onClick={openCommandPalette}
          className="lg:hidden p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
          aria-label={t('header.searchOrRun')}
          title={t('header.quickSearchMobile')}
        >
          <Search className="w-4 h-4" />
        </button>

        {/* Desktop Quick Command Palette Button */}
        <button
          type="button"
          onClick={openCommandPalette}
          className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-surface-border bg-muted/40 hover:bg-muted/70 text-xs text-foreground-muted transition-colors"
          title={t('header.searchOrRun')}
        >
          <Search className="w-3.5 h-3.5" />
          <span>{t('header.quickSearch')}</span>
          <kbd className="px-1.5 py-0.5 rounded bg-surface-elevated border border-surface-border text-[10px] font-mono text-foreground-muted shadow-sm">
            ⌘K
          </kbd>
        </button>

        {/* Internationalization Language Switcher (Task 10.7.1) */}
        <LanguageSwitcher variant="header" />

        {/* Quick Light/Dark Mode Switcher (Task 10.7.5) */}
        <ThemeSwitcher />

        {/* Real-Time Wallet Balance Badge (Task 10.1.5) */}
        <WalletBadge variant="header" />

        {/* Multi-Tenant Workspace Switcher Dropdown */}
        <WorkspaceSwitcher />

        {/* User Profile & Session Dropdown */}
        <UserProfileDropdown onNavigate={onNavigate} />
      </div>
    </header>
  );
};
